import { statusView } from './format.js';
import { fmtMoney, pausedAtText, pausedReasonLine } from './view.js';
/** Статус строки: точка + слово + причина второй строкой (макет Main, колонка «Статус»). */
export function rowStatus(row) {
    const v = statusView(row);
    if (v.label === 'Удалено')
        return { tone: 'red', word: 'Удалено', sub: null, warn: false };
    if (v.label === 'В архиве')
        return { tone: 'slate', word: 'В архиве', sub: null, warn: false };
    if (v.paused) {
        const at = pausedAtText(row.pausedReason);
        const reason = pausedReasonLine(row.pausedReason);
        const metaErr = row.pausedReason?.kind === 'meta-error';
        return {
            tone: metaErr ? 'red' : 'slate',
            word: metaErr ? 'Пауза · ошибка Meta' : at ? `Пауза · ${at}` : 'Пауза',
            sub: metaErr ? null : reason,
            warn: false,
        };
    }
    if (v.tone === 'red')
        return { tone: 'red', word: 'Есть проблемы', sub: null, warn: false };
    if (row.allChildrenPaused)
        return {
            tone: 'green',
            word: row.kind === 'campaign' ? 'Работает' : 'Включено',
            sub: row.kind === 'campaign' ? '⚠ все группы на паузе' : '⚠ все объявления на паузе',
            warn: true,
        };
    if (!row.running && row.kind !== 'ad')
        return { tone: 'amber', word: 'Включена, не крутится', sub: null, warn: false };
    if (!row.running && row.kind === 'ad')
        return { tone: 'amber', word: 'Включено, не крутится', sub: null, warn: false };
    return { tone: 'green', word: 'Работает', sub: null, warn: false };
}
const EMPTY_CPP = null;
/** «Бюджет в день» кампании (только чтение): $400; на группах — «Σ $300» и «на группах». */
export function campaignBudget(b, currency) {
    if (b.level === null)
        return { main: '—', sub: null };
    if (b.level === 'adset')
        return b.dailyMinor !== null
            ? { main: `Σ ${fmtMoney(b.dailyMinor, currency, 'whole')}`, sub: 'на группах' }
            : { main: 'на группах', sub: 'в каждой свой' };
    if (b.dailyMinor !== null)
        return { main: fmtMoney(b.dailyMinor, currency, 'whole'), sub: null };
    if (b.lifetimeMinor !== null)
        return { main: fmtMoney(b.lifetimeMinor, currency, 'whole'), sub: 'на весь срок' };
    return { main: '—', sub: null };
}
/** Бюджет группы: свой, а если задан на кампании — «в кампании». */
function adSetBudget(s, currency) {
    if (s.dailyBudgetMinor !== null)
        return { main: fmtMoney(s.dailyBudgetMinor, currency, 'whole'), sub: null };
    if (s.lifetimeBudgetMinor !== null)
        return { main: fmtMoney(s.lifetimeBudgetMinor, currency, 'whole'), sub: 'на весь срок' };
    return { main: 'в кампании', sub: null };
}
export function campaignToRow(c, expanded, currency) {
    const v = statusView(c);
    const paused = c.counts.adSets - c.counts.adSetsActive;
    const groups = c.counts.adSets;
    return {
        key: `c-${c.campaignId}`,
        kind: 'campaign',
        depth: 0,
        id: c.campaignId,
        campaignId: c.campaignId,
        name: c.name,
        status: c.status,
        effectiveStatus: c.effectiveStatus,
        running: c.running,
        managed: c.managedByLaunch,
        on: c.status === 'ACTIVE',
        paused: v.paused,
        gone: v.label === 'Удалено' || v.label === 'В архиве',
        budget: campaignBudget(c.budget, currency),
        budgetMinor: c.budget.dailyMinor ?? c.budget.lifetimeMinor,
        spendToday: c.spend.todayMinor,
        purchasesToday: c.purchases.today,
        trial: c.purchases.trialBreakdown,
        cppToday: c.cppTodayMinor,
        cpp: c.cpp ?? EMPTY_CPP,
        pauseDays: c.pauseDays,
        pausedReason: c.pausedReason,
        payback: c.payback,
        allChildrenPaused: c.allChildrenPaused,
        groupsLine: groups > 0
            ? {
                text: paused > 0 ? `${groups} гр. · ${paused} на паузе` : `${groups} гр.`,
                bad: paused > 0 && c.status === 'ACTIVE',
            }
            : null,
        expandable: groups > 0,
        expanded,
        campaign: c,
        matchedIn: c.matchedIn,
    };
}
export function adSetToRow(s, campaignId, expanded, currency) {
    const v = statusView(s);
    return {
        key: `s-${s.adSetId}`,
        kind: 'adset',
        depth: 1,
        id: s.adSetId,
        campaignId,
        name: s.name,
        status: s.status,
        effectiveStatus: s.effectiveStatus,
        running: s.running,
        managed: s.managedByLaunch,
        on: s.status === 'ACTIVE',
        paused: v.paused,
        gone: v.label === 'Удалено' || v.label === 'В архиве',
        budget: adSetBudget(s, currency),
        budgetMinor: s.dailyBudgetMinor ?? s.lifetimeBudgetMinor,
        spendToday: s.spend.todayMinor,
        purchasesToday: s.purchases.today,
        trial: s.purchases.trialBreakdown,
        cppToday: s.cppTodayMinor,
        cpp: s.cpp ?? EMPTY_CPP,
        pauseDays: s.pauseDays,
        pausedReason: s.pausedReason,
        payback: null,
        allChildrenPaused: s.allChildrenPaused,
        groupsLine: s.ads.length > 0 ? { text: `${s.ads.length} объявл.`, bad: false } : null,
        expandable: s.ads.length > 0,
        expanded,
        matchedIn: s.matchedIn,
    };
}
export function adToRow(a, campaignId) {
    const v = statusView({ ...a, running: (a.effectiveStatus ?? a.status) === 'ACTIVE' });
    return {
        key: `a-${a.adId}`,
        kind: 'ad',
        depth: 2,
        id: a.adId,
        campaignId,
        name: a.name,
        status: a.status,
        effectiveStatus: a.effectiveStatus,
        running: (a.effectiveStatus ?? a.status) === 'ACTIVE',
        managed: a.managedByLaunch,
        on: a.status === 'ACTIVE',
        paused: v.paused,
        gone: v.label === 'Удалено' || v.label === 'В архиве',
        budget: { main: 'в кампании', sub: null },
        budgetMinor: null,
        spendToday: a.spend.todayMinor,
        purchasesToday: a.purchases.today,
        trial: a.purchases.trialBreakdown,
        cppToday: a.cppTodayMinor,
        cpp: a.cpp ?? EMPTY_CPP,
        pauseDays: a.pauseDays,
        pausedReason: a.pausedReason,
        payback: null,
        allChildrenPaused: false,
        groupsLine: null,
        expandable: false,
        expanded: false,
        thumbnailUrl: a.thumbnailUrl,
        matchedIn: a.matchedIn,
    };
}
/** Значение колонки для сортировки детей внутри родителя (тот же ключ, что у сервера). */
export function sortValue(row, key) {
    switch (key) {
        case 'name':
            return row.name.toLocaleLowerCase('ru');
        case 'status':
            // работают → включены, но не крутятся → на паузе → архив
            return row.gone ? 0 : row.paused ? 1 : row.running ? 3 : 2;
        case 'pauseDays':
            return row.pauseDays;
        case 'cppD1':
            return row.cpp?.d1 ?? null;
        case 'cppD7':
            return row.cpp?.d7 ?? null;
        case 'cppD30':
            return row.cpp?.d30 ?? null;
        case 'cppD90':
            return row.cpp?.d90 ?? null;
        case 'budget':
            return row.budgetMinor;
        case 'spendToday':
            return row.spendToday;
        case 'purchases':
            return row.purchasesToday;
        case 'cppToday':
            return row.cppToday;
        case 'payback':
            return row.payback?.perDollar ?? null;
        default:
            return null;
    }
}
/** Сортирует детей внутри родителя; пустые значения — внизу при любом направлении. */
export function sortRows(items, sort) {
    if (!sort || sort.key === 'default')
        return [...items];
    const sign = sort.dir === 'asc' ? 1 : -1;
    return items
        .map((it, i) => ({ it, i, v: sortValue(it.row, sort.key) }))
        .sort((a, b) => {
        const ea = a.v === null || a.v === undefined;
        const eb = b.v === null || b.v === undefined;
        if (ea || eb)
            return ea === eb ? a.i - b.i : ea ? 1 : -1;
        const c = typeof a.v === 'number' && typeof b.v === 'number'
            ? a.v - b.v
            : String(a.v).localeCompare(String(b.v), 'ru');
        return c !== 0 ? sign * c : a.i - b.i;
    })
        .map((x) => x.it);
}
//# sourceMappingURL=tree.js.map